import { useEffect, useMemo, useRef } from 'react';
import type { RefObject } from 'react';
import type { PredictedAgeGroup, TrackSessionEventRequest } from '@volt/shared';
import { classifyAgeGroup } from '../lib/ageClassifier.js';
import type { TrackingBuffer } from '../lib/trackingBuffer.js';

interface SessionMetrics {
  modelsViewed: number;
  modelNamesCopied: number;
  searchesCount: number;
  favoritesAdded: number;
  modelCardClicks: number;
  aiRequestsSent: number;
  internetSpeedMbps: number | null;
  country: string | null;
  timezone: string | null;
  maxScrollDepthPct: number;
  rageClicks: number;
  errorTapCount: number;
  avgNavigationTimeMs: number | null;
  scrollSpeedPxPerSec: number | null;
  predictedAgeGroup: PredictedAgeGroup;
}

export interface SessionEventTracker {
  trackSearch: () => void;
  trackFavoriteAdded: () => void;
  trackModelView: (count?: number) => void;
  trackModelNameCopied: () => void;
  trackModelCardClick: () => void;
  trackAiRequest: () => void;
  recordNavigationDuration: (durationMs: number) => void;
}

function isInteractive(node: HTMLElement | null): boolean {
  return Boolean(node?.closest('button, a, input, textarea, select, [role="button"], [role="link"]'));
}

function average(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function median(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1]! + sorted[mid]!) / 2 : sorted[mid]!;
}

function standardDeviation(values: number[]): number | null {
  if (values.length === 0) return null;
  const mean = average(values)!;
  return Math.sqrt(values.reduce((sum, value) => sum + (value - mean) ** 2, 0) / values.length);
}

export function useSessionEvents(
  userId: string | undefined,
  sessionId: string | null,
  buffer: TrackingBuffer,
  containerRef: RefObject<HTMLElement | null>,
): SessionEventTracker {
  const metricsRef = useRef<SessionMetrics>({
    modelsViewed: 0,
    modelNamesCopied: 0,
    searchesCount: 0,
    favoritesAdded: 0,
    modelCardClicks: 0,
    aiRequestsSent: 0,
    internetSpeedMbps: null,
    country: null,
    timezone: null,
    maxScrollDepthPct: 0,
    rageClicks: 0,
    errorTapCount: 0,
    avgNavigationTimeMs: null,
    scrollSpeedPxPerSec: null,
    predictedAgeGroup: 'unknown',
  });
  const dirtyRef = useRef(false);
  const startedAtRef = useRef(Date.now());
  const lastActionAtRef = useRef<number | null>(null);
  const actionIntervalsRef = useRef<number[]>([]);
  const actionTimesRef = useRef<number[]>([]);
  const navigationDurationsRef = useRef<number[]>([]);
  const rageClicksRef = useRef<Array<{ x: number; y: number; at: number }>>([]);
  const scrollSamplesRef = useRef<number[]>([]);

  const buildPayload = useMemo(
    () => (): TrackSessionEventRequest | null => {
      if (!sessionId) return null;
      const avgInteractionMs = average(actionIntervalsRef.current);
      const medianInteractionMs = median(actionIntervalsRef.current);
      const stdInteractionMs = standardDeviation(actionIntervalsRef.current);
      const avgNavigationTimeMs = average(navigationDurationsRef.current);
      const scrollSpeedPxPerSec = average(scrollSamplesRef.current);
      const elapsedMinutes = Math.max((Date.now() - startedAtRef.current) / 60_000, 1 / 60);
      const eventsPerMinute = actionTimesRef.current.length / elapsedMinutes;
      const base: TrackSessionEventRequest = {
        sessionId,
        modelsViewed: metricsRef.current.modelsViewed,
        modelNamesCopied: metricsRef.current.modelNamesCopied,
        searchesCount: metricsRef.current.searchesCount,
        favoritesAdded: metricsRef.current.favoritesAdded,
        modelCardClicks: metricsRef.current.modelCardClicks,
        aiRequestsSent: metricsRef.current.aiRequestsSent,
        internetSpeedMbps: metricsRef.current.internetSpeedMbps,
        country: metricsRef.current.country,
        timezone: metricsRef.current.timezone,
        maxScrollDepthPct: metricsRef.current.maxScrollDepthPct,
        rageClicks: metricsRef.current.rageClicks,
        avgInteractionMs,
        medianInteractionMs,
        stdInteractionMs,
        errorTapCount: metricsRef.current.errorTapCount,
        eventsPerMinute,
        avgNavigationTimeMs,
        scrollSpeedPxPerSec,
        predictedAgeGroup: classifyAgeGroup({
          avgInteractionMs,
          medianInteractionMs,
          stdInteractionMs,
          maxScrollDepthPct: metricsRef.current.maxScrollDepthPct,
          scrollSpeedPxPerSec,
          rageClicks: metricsRef.current.rageClicks,
          eventsPerMinute,
          avgNavigationTimeMs,
          aiRequestsSent: metricsRef.current.aiRequestsSent,
          searchesCount: metricsRef.current.searchesCount,
        }),
      };
      metricsRef.current.predictedAgeGroup = base.predictedAgeGroup ?? 'unknown';
      metricsRef.current.avgNavigationTimeMs = avgNavigationTimeMs;
      metricsRef.current.scrollSpeedPxPerSec = scrollSpeedPxPerSec;
      return base;
    },
    [sessionId],
  );

  useEffect(() => {
    if (!userId || !sessionId) return;
    startedAtRef.current = Date.now();
    metricsRef.current.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
    const connection = navigator as Navigator & { connection?: { downlink?: number } };
    metricsRef.current.internetSpeedMbps =
      typeof connection.connection?.downlink === 'number' ? connection.connection.downlink : null;
    dirtyRef.current = true;

    buffer.enqueue({ type: 'track-session-event-create', payload: buildPayload() ?? { sessionId } });

    const pointerHandler = (event: PointerEvent) => {
      const now = Date.now();
      actionTimesRef.current.push(now);
      if (lastActionAtRef.current != null) actionIntervalsRef.current.push(now - lastActionAtRef.current);
      lastActionAtRef.current = now;
      const target = event.target as HTMLElement | null;
      if (!isInteractive(target)) metricsRef.current.errorTapCount += 1;
      rageClicksRef.current = rageClicksRef.current.filter(
        (click) => now - click.at <= 1000 && Math.hypot(click.x - event.clientX, click.y - event.clientY) <= 50,
      );
      const prior = rageClicksRef.current.length;
      rageClicksRef.current.push({ x: event.clientX, y: event.clientY, at: now });
      if (prior < 2 && rageClicksRef.current.length >= 3) metricsRef.current.rageClicks += 1;
      dirtyRef.current = true;
    };

    const keyHandler = () => {
      const now = Date.now();
      actionTimesRef.current.push(now);
      if (lastActionAtRef.current != null) actionIntervalsRef.current.push(now - lastActionAtRef.current);
      lastActionAtRef.current = now;
      dirtyRef.current = true;
    };

    let lastScrollTop = window.scrollY;
    let lastScrollAt = Date.now();
    const scrollHandler = () => {
      const root = containerRef.current;
      const scrollTop = root ? root.scrollTop : window.scrollY;
      const viewportHeight = root ? root.clientHeight : window.innerHeight;
      const scrollHeight = root ? root.scrollHeight : document.documentElement.scrollHeight;
      const maxDepth = scrollHeight > 0 ? ((scrollTop + viewportHeight) / scrollHeight) * 100 : 0;
      metricsRef.current.maxScrollDepthPct = Math.max(metricsRef.current.maxScrollDepthPct, Math.min(100, maxDepth));
      const now = Date.now();
      const deltaPx = Math.abs(scrollTop - lastScrollTop);
      const deltaMs = Math.max(now - lastScrollAt, 1);
      scrollSamplesRef.current.push((deltaPx / deltaMs) * 1000);
      lastScrollTop = scrollTop;
      lastScrollAt = now;
      dirtyRef.current = true;
    };

    const syncTimer = window.setInterval(() => {
      if (!dirtyRef.current) return;
      const payload = buildPayload();
      if (!payload) return;
      buffer.enqueue({ type: 'track-session-event-update', payload });
      dirtyRef.current = false;
    }, 5000);

    document.addEventListener('pointerdown', pointerHandler, true);
    document.addEventListener('keydown', keyHandler, true);
    window.addEventListener('scroll', scrollHandler, { passive: true });

    return () => {
      document.removeEventListener('pointerdown', pointerHandler, true);
      document.removeEventListener('keydown', keyHandler, true);
      window.removeEventListener('scroll', scrollHandler);
      window.clearInterval(syncTimer);
    };
  }, [buffer, buildPayload, containerRef, sessionId, userId]);

  return {
    trackSearch() {
      metricsRef.current.searchesCount += 1;
      dirtyRef.current = true;
    },
    trackFavoriteAdded() {
      metricsRef.current.favoritesAdded += 1;
      dirtyRef.current = true;
    },
    trackModelView(count = 1) {
      metricsRef.current.modelsViewed += count;
      dirtyRef.current = true;
    },
    trackModelNameCopied() {
      metricsRef.current.modelNamesCopied += 1;
      dirtyRef.current = true;
    },
    trackModelCardClick() {
      metricsRef.current.modelCardClicks += 1;
      dirtyRef.current = true;
    },
    trackAiRequest() {
      metricsRef.current.aiRequestsSent += 1;
      dirtyRef.current = true;
    },
    recordNavigationDuration(durationMs: number) {
      if (durationMs > 0) navigationDurationsRef.current.push(durationMs);
      dirtyRef.current = true;
    },
  };
}
